import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, Package, RefreshCw, Store } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    start: firstDay.toISOString().slice(0, 10),
    end: now.toISOString().slice(0, 10),
  };
};

function MetricCard({ label, value, helper, icon: Icon, tone = 'neutral' }) {
  const colorClass = {
    positive: 'text-emerald-300 border-emerald-400/20 bg-emerald-500/10',
    warning: 'text-[#D4AF37] border-[#D4AF37]/25 bg-[#D4AF37]/10',
    negative: 'text-red-300 border-red-400/20 bg-red-500/10',
    neutral: 'text-white border-white/10 bg-white/[0.03]',
  }[tone];

  return (
    <div className="border border-white/10 bg-zinc-950/45 p-5">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
        <div className={`flex h-9 w-9 items-center justify-center rounded-sm border ${colorClass}`}>
          <Icon className="h-4 w-4" strokeWidth={1.7} />
        </div>
      </div>
      <p className={`text-2xl font-semibold ${tone === 'positive' ? 'text-emerald-300' : tone === 'warning' ? 'text-[#D4AF37]' : tone === 'negative' ? 'text-red-300' : 'text-white'}`}>
        {value}
      </p>
      {helper ? <p className="mt-2 text-xs text-zinc-500">{helper}</p> : null}
    </div>
  );
}

export default function ConsignmentReport() {
  const defaultRange = useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const { data } = await axios.get(`${API}/reports/profit-loss?${params.toString()}`, {
        withCredentials: true,
      });
      setReport(data);
    } catch (error) {
      console.error('Failed to fetch consignment report:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil laporan produk titipan.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = report?.summary || {};
  const consignmentOrders = useMemo(
    () => (report?.product_orders || []).filter((order) => Number(order.consignment_revenue || 0) > 0),
    [report]
  );
  const consignmentProfit = Number(summary.consignment_profit || 0);

  return (
    <DashboardLayout>
      <Header title="Laporan Titipan" />
      <div className="p-8" data-testid="consignment-report-page">
        <div className="mb-6 border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.14),transparent_28%),linear-gradient(135deg,rgba(255,255,255,0.035),rgba(9,9,11,0.82))]">
          <div className="flex flex-col gap-6 p-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <Badge className="mb-3 rounded-sm border border-sky-400/30 bg-sky-400/10 text-sky-300 hover:bg-sky-400/10">
                Konsinyasi
              </Badge>
              <h2 className="text-2xl font-semibold text-white">Laporan Produk Titipan</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-500">
                Laporan ini terpisah dari Profit Loss gym. Gunakan untuk melihat penjualan titipan, nominal setor ke penitip, dan margin titipan.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-[160px_160px_auto]">
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Dari</p>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-11 rounded-sm border-white/10 bg-black/25 text-white" />
              </div>
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Sampai</p>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-11 rounded-sm border-white/10 bg-black/25 text-white" />
              </div>
              <Button onClick={fetchReport} disabled={loading} className="self-end rounded-sm bg-sky-400 px-6 text-black hover:bg-sky-300">
                <RefreshCw className="mr-2 h-4 w-4" />
                Terapkan
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky-300 border-t-transparent" />
          </div>
        ) : (
          <>
            <div className="mb-6 grid gap-4 md:grid-cols-2">
              <MetricCard label="Penjualan Titipan" value={formatCurrency(summary.consignment_revenue || 0)} helper={`${consignmentOrders.length} order berisi produk titipan`} icon={Store} tone="warning" />
              <MetricCard label="Margin Titipan" value={formatCurrency(consignmentProfit)} helper="Selisih penjualan titipan dikurangi setor penitip." icon={ArrowUpRight} tone={consignmentProfit >= 0 ? 'positive' : 'negative'} />
            </div>

            <div className="border border-white/10 bg-zinc-950/40">
              <div className="border-b border-white/10 px-5 py-4">
                <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                  <Package className="h-4 w-4 text-sky-300" strokeWidth={1.6} />
                  Order Produk Titipan
                </h3>
                <p className="mt-1 text-xs text-zinc-500">Hanya menampilkan order yang memiliki produk titipan.</p>
              </div>
              <div className="divide-y divide-white/5">
                {consignmentOrders.length > 0 ? (
                  consignmentOrders.map((order) => (
                    <div key={order.id} className="grid gap-4 px-5 py-4 md:grid-cols-[1fr_auto_auto] md:items-center">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-white">{order.customer}</p>
                        <p className="mt-1 text-xs text-zinc-500">{order.source || 'POS'} - {formatDate(order.date)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">Penjualan</p>
                        <p className="mt-1 text-sm font-semibold text-white">{formatCurrency(order.consignment_revenue || 0)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">Produk Titipan</p>
                        <p className="mt-1 text-xs text-zinc-400">
                          {(order.hpp_details || [])
                            .filter((item) => String(item.sale_type || '').toUpperCase() === 'CONSIGNMENT')
                            .map((item) => `${item.name} x${item.quantity}`)
                            .join(', ') || '-'}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-5 py-12 text-center text-sm text-zinc-600">Belum ada penjualan produk titipan pada periode ini.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
